import userResources from "api/userResources";
import React, { useEffect, useState } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import { useNavigate } from "react-router-dom";
import Datepicker from "react-tailwindcss-datepicker";
import Select from "react-tailwindcss-select";
import { toast } from "react-toastify";

const flightTypes = [
  { label: "Economy", value: "economy" },
  { label: "Premium Economy", value: "premium_economy" },
  { label: "Business", value: "business" },
];

export default function RoundTripFormForPhone() {
  const navigate = useNavigate();
  const [airpotsFrom, setAirpotsFrom] = useState(null);
  const [airpotsTo, setAirpotsTo] = useState(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [selectedFlightType, setSelectedFlightType] = useState("economy");
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const [depart, setDepart] = useState({
    startDate: today,
    endDate: new Date().setMonth(11), // December
  });

  const [returnFlight, setReturnFlight] = useState({
    startDate: tomorrow,
    endDate: new Date().setMonth(11), // December
  });
  const [airport, setAirport] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState();
  const [airportSearch, setAirportSearch] = useState();
  const [directFlight, setDirectFlight] = useState(true);

  const handleFlightTypeChange = (event) => {
    setSelectedFlightType(event.target.value);
  };
  const handleDirectflight = (event) => {
    setDirectFlight(event.target.checked);
  };

  // ✅ On changing departure
  const departFun = (newValue) => {
    setDepart(newValue); // This will auto-trigger the useEffect
  };

  // ✅ On changing return date manually
  const setReturnFlightFun = (newValue) => {
    const departDate = new Date(depart.startDate);
    const selectedReturnDate = new Date(newValue.startDate);

    // Normalize
    departDate.setHours(0, 0, 0, 0);
    selectedReturnDate.setHours(0, 0, 0, 0);

    if (selectedReturnDate <= departDate) {
      toast.error(
        "Return date must be at least one day after the departure date."
      );
      return;
    }

    setReturnFlight(newValue);
  };

  const getAirport = async (filter) => {
    const params = {
      iata: filter,
    };
    try {
      const res = await userResources.airport(params);
      const data = res.data.data.map((item) => ({
        value: item?.id,
        label: item?.iata + " " + item?.name,
      }));
      setAirport(data);
    } catch (error) {
      console.error("Failed to fetch airport data:", error);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const data = {
      form_world_airport_id: airpotsFrom?.value,
      to_world_airport_id: airpotsTo?.value,
      departure_date: depart?.startDate,
      return_date: returnFlight?.startDate,
      adults: parseInt(adults),
      children: parseInt(children),
      infants: parseInt(infants),
      travel_type: selectedFlightType,
      phone: phoneNumber,
      direct_flight: directFlight,
    };

    try {
      await userResources.submitSearchEnquiry(data);
      // navigate to loading screen with form data
      navigate("/loading", {
        state: {
          fromLabel: airpotsFrom?.label,
          toLabel: airpotsTo?.label,
          departure_date: depart?.startDate,
          return_date: returnFlight?.startDate,
          adults: parseInt(adults),
          children: parseInt(children),
          infants: parseInt(infants),
          travel_type: selectedFlightType,
          phone: phoneNumber,
          direct_flight: directFlight,
        },
      });
    } catch (err) {
      toast.error(
        "❌ Submission failed. Please try again or call us at 905-244-1300."
      );
    }
  };

  // 🔁 Update returnFlight.startDate automatically if it's before or same as depart.startDate
  useEffect(() => {
    const departDate = new Date(depart.startDate);
    const returnDate = new Date(returnFlight.startDate);

    // Normalize both to midnight to avoid time-based comparison issues
    departDate.setHours(0, 0, 0, 0);
    returnDate.setHours(0, 0, 0, 0);

    // If depart is the same or after return, fix return to be next day of depart
    if (departDate > returnDate) {
      const nextDay = new Date(departDate);
      nextDay.setDate(departDate.getDate() + 1);

      setReturnFlight((prev) => ({
        ...prev,
        startDate: nextDay,
      }));
    }
  }, [depart.startDate]);

  useEffect(() => {
    if (airportSearch) {
      getAirport(airportSearch);
    }
  }, [airportSearch]);

  return (
    <section className="text-gray-800">
      <form onSubmit={onSubmit}>
        <div>
          <div className="rounded-xl bg-gray-100 p-1 px-3">
            <label className="text-md">FROM</label>
            <Select
              value={airpotsFrom}
              onChange={setAirpotsFrom}
              options={airport}
              isSearchable
              onSearchInputChange={(e) => {
                setAirportSearch(e.target.value);
              }}
              classNames={{
                menuButton: ({ isDisabled }) =>
                  `flex w-full items-center relative -left-3 border-0 text-lg overflow-hidden text-gray-900 transition-all outline-none duration-300 ${
                    isDisabled
                      ? "bg-gray-200"
                      : "bg-transparent hover:border-gray-400"
                  }`,
                menu: "absolute -top-0 z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700",
                listItem: ({ isSelected }) =>
                  `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
                    isSelected
                      ? "text-white bg-blue-500"
                      : "text-gray-500 hover:bg-blue-100 hover:text-blue-500"
                  }`,
              }}
              formatGroupLabel={(data) => (
                <div className="flex items-center justify-between py-2 text-xs">
                  <span className="font-bold">{data.label}</span>
                  <span className="flex h-5 items-center justify-center rounded-full bg-gray-200 p-1.5">
                    {data.options.length} 👉 Airpots
                  </span>
                </div>
              )}
              formatOptionLabel={(data) => (
                <li
                  className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                    !data.isSelected
                      ? "bg-gray-200 text-gray-900"
                      : "bg-blue-800 text-gray-200"
                  }`}
                >
                  {data.label}
                </li>
              )}
            />
          </div>

          <div className="my-2 rounded-xl bg-gray-100 p-1 px-3">
            <label className="text-md" htmlFor="to">
              TO
            </label>
            <Select
              id="to"
              value={airpotsTo}
              onChange={setAirpotsTo}
              options={airport}
              isSearchable
              onSearchInputChange={(e) => {
                setAirportSearch(e.target.value);
              }}
              classNames={{
                menuButton: ({ isDisabled }) =>
                  `flex w-full relative -left-3 border-0 text-lg overflow-hidden text-gray-900 transition-all outline-none duration-300 ${
                    isDisabled
                      ? "bg-gray-200"
                      : "bg-transparent hover:border-gray-400"
                  }`,
                menu: "absolute -top-0 z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700",
                listItem: ({ isSelected }) =>
                  `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
                    isSelected
                      ? "text-white bg-blue-500"
                      : "text-gray-500 hover:bg-blue-100 hover:text-blue-500"
                  }`,
              }}
              formatGroupLabel={(data) => (
                <div className="flex items-center justify-between py-2 text-xs">
                  <span className="font-bold">{data.label}</span>
                  <span className="flex h-5 items-center justify-center rounded-full bg-gray-200 p-1.5">
                    {data.options.length} 👉 Airpots
                  </span>
                </div>
              )}
              formatOptionLabel={(data) => (
                <li
                  className={`mb-1 block cursor-pointer select-none truncate rounded px-2 py-2 transition duration-200 ${
                    !data.isSelected
                      ? "bg-gray-200 text-gray-900"
                      : "bg-blue-800 text-gray-200"
                  }`}
                >
                  {data.label}
                </li>
              )}
            />
          </div>

          <div className="my-2 rounded-xl bg-gray-100 p-1 px-3">
            <div className="flex items-center gap-2">
              <FaCalendarAlt />
              <label className="text-md">DEPART</label>
            </div>
            <Datepicker
              inputClassName="p-0 pt-2 pb-1 bg-transparent outline-0 text-gray-900 w-72 cursor-pointer"
              useRange={false}
              asSingle
              value={depart}
              showShortcuts={false}
              onChange={departFun}
            />
          </div>

          <div className="my-2 rounded-xl bg-gray-100 p-1 px-3">
            <div className="flex items-center gap-2">
              <FaCalendarAlt />
              <label className="text-md">RETURN</label>
            </div>
            <Datepicker
              inputClassName="p-0 pt-2 pb-1 bg-transparent outline-0 text-gray-900 w-72 cursor-pointer"
              useRange={false}
              asSingle
              value={returnFlight}
              showShortcuts={false}
              showFooter={false}
              configs={{
                footer: { cancel: "Cancel", apply: "Apply" },
              }}
              minDate={
                new Date(
                  new Date(depart.startDate).setDate(
                    new Date(depart.startDate).getDate() + 1
                  )
                )
              }
              onChange={setReturnFlightFun}
            />
          </div>

          <div className="my-2 rounded-xl bg-gray-100 p-1 px-3">
            <div className="flex items-center gap-2">
              <FaCalendarAlt />
              <label className="text-md">TRAVELLERS</label>
            </div>
            <div className="dropdown w-full">
              <div tabIndex={0} role="button" className="cursor-pointer">
                <h3 className="text-lg">
                  {adults + children + infants} Travellers
                </h3>
                <p className="text-sm">
                  {selectedFlightType === "economy"
                    ? "Economy"
                    : selectedFlightType === "premium_economy"
                    ? "Premium Economy"
                    : selectedFlightType === "business"
                    ? "Business"
                    : ""}
                </p>
              </div>
              <div
                tabIndex={0}
                className="menu dropdown-content z-[1] w-full rounded-box bg-white p-2 shadow"
              >
                {/** Adults */}
                <div className="cursor-pointer rounded-md border-b p-2 hover:bg-gray-100">
                  <div className="flex items-center justify-between">
                    <h4>Adults</h4>
                    <h4>{adults}</h4>
                    <div className="flex items-center rounded-md border">
                      <div
                        className="cursor-pointer border border-r-0 p-1 px-3 hover:bg-red-500 hover:text-white"
                        onClick={() => adults > 1 && setAdults(adults - 1)}
                      >
                        –
                      </div>
                      <div
                        className="border-s-0 cursor-pointer border p-1 px-3 hover:bg-red-500 hover:text-white"
                        onClick={() => setAdults(adults + 1)}
                      >
                        +
                      </div>
                    </div>
                  </div>
                </div>
                {/** Children */}
                <div className="cursor-pointer rounded-md border-b p-2 hover:bg-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4>Children</h4>
                      <h6 className="text-gray-400">2-12 Years</h6>
                    </div>
                    <h4>{children}</h4>
                    <div className="flex items-center rounded-md border">
                      <div
                        className="cursor-pointer border border-r-0 p-1 px-3 hover:bg-red-500 hover:text-white"
                        onClick={() =>
                          children > 0 && setChildren(children - 1)
                        }
                      >
                        –
                      </div>
                      <div
                        className="border-s-0 cursor-pointer border p-1 px-3 hover:bg-red-500 hover:text-white"
                        onClick={() => setChildren(children + 1)}
                      >
                        +
                      </div>
                    </div>
                  </div>
                </div>
                {/** Infants */}
                <div className="cursor-pointer rounded-md border-b p-2 hover:bg-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4>Infants</h4>
                      <h6 className="text-gray-400">0-23 Months</h6>
                    </div>
                    <h4>{infants}</h4>
                    <div className="flex items-center rounded-md border">
                      <div
                        className="cursor-pointer border border-r-0 p-1 px-3 hover:bg-red-500 hover:text-white"
                        onClick={() => infants > 0 && setInfants(infants - 1)}
                      >
                        –
                      </div>
                      <div
                        className="border-s-0 cursor-pointer border p-1 px-3 hover:bg-red-500 hover:text-white"
                        onClick={() => setInfants(infants + 1)}
                      >
                        +
                      </div>
                    </div>
                  </div>
                </div>
                {/** Flight Class Radios */}
                <div className="py-1 px-1">
                  {flightTypes.map((type) => (
                    <label
                      key={type.value}
                      className="flex cursor-pointer items-center gap-2 rounded-md py-1 px-1 hover:bg-gray-100"
                    >
                      <input
                        type="radio"
                        name="flightType"
                        value={type.value}
                        checked={selectedFlightType === type.value}
                        onChange={handleFlightTypeChange}
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/** Direct Flight Checkbox */}
          <div className="relative mt-4 space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex sm:items-start sm:gap-6 sm:space-y-0 sm:p-6">
            {/* Checkbox - Modern Toggle Style */}
            <div className="flex w-full items-center justify-between sm:w-1/3 sm:justify-start">
              <label
                htmlFor="directFlight"
                className="flex cursor-pointer items-center"
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    id="directFlight"
                    name="directFlight"
                    checked={directFlight}
                    onChange={handleDirectflight}
                    className="sr-only"
                  />
                  <div
                    className={`block h-8 w-14 rounded-full transition-colors ${
                      directFlight ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  ></div>
                  <div
                    className={`absolute left-1 top-1 h-6 w-6 rounded-full bg-white transition-transform ${
                      directFlight ? "translate-x-6" : "translate-x-0"
                    }`}
                  ></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700 sm:text-base">
                  Direct Flight Only
                </span>
              </label>
            </div>

            {/* Phone Input - With Label */}
            <div className="w-full sm:w-2/3">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <PhoneInputWithCountrySelect
                defaultCountry="CA"
                value={phoneNumber}
                onChange={(value) => setPhoneNumber(value)}
                placeholder="Enter phone number"
                className="w-full"
                inputClassName="h-12 w-full rounded-lg border border-gray-300 !bg-white px-3 text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/** Submit */}
        <div className="mt-4 flex w-full justify-center">
          <button type="submit" className="btn-grad w-full">
            SUBMIT
          </button>
        </div>
      </form>
      {loading && (
        <div className="my-4 flex items-center justify-center gap-2 text-center font-semibold text-blue-600">
          <svg
            className="h-5 w-5 animate-spin text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
          We’re finding the best flight options for you...
        </div>
      )}
    </section>
  );
}
